import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { EVALUATION_PLAN_WEB_DOCUMENT_CAPTURE_CSS } from './evaluationPlanWebDocumentCaptureCss';
import { balanceEvaluationPlanWebDocumentA4Layout } from './evaluationPlanWebDocumentA4Layout';
import { prepareEvaluationPlanWebDocumentPageClone } from './evaluationPlanWebDocumentExportClone';
import { printStandaloneHtmlDocument } from './modalDocumentExport';

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;

const EVALUATION_PLAN_WEB_DOC_PRINT_SHELL_CSS = `
  @page {
    size: ${A4_WIDTH_MM}mm ${A4_HEIGHT_MM}mm;
    margin: 0;
  }
  html,
  body {
    margin: 0;
    padding: 0;
    background: #ffffff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  body {
    display: block;
  }
  .cp-evaluation-plan-web-doc-print-port {
    width: ${A4_WIDTH_MM}mm;
    min-height: ${A4_HEIGHT_MM}mm;
    margin: 0 auto;
    overflow: visible;
    box-sizing: border-box;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 0;
  }
  .cp-evaluation-plan-web-doc-page {
    width: ${A4_WIDTH_MM}mm !important;
    max-width: ${A4_WIDTH_MM}mm !important;
    height: ${A4_HEIGHT_MM}mm !important;
    min-height: ${A4_HEIGHT_MM}mm !important;
    aspect-ratio: 210 / 297 !important;
    margin: 0 !important;
    overflow: hidden !important;
    box-shadow: none !important;
    display: flex !important;
    flex-direction: column !important;
    page-break-inside: avoid;
    break-inside: avoid;
    box-sizing: border-box !important;
  }
  .cp-evaluation-plan-web-doc-page-main {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
  }
  .cp-evaluation-plan-web-doc-example-answer-section {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }
  .cp-evaluation-plan-web-doc-example-answer-body {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }
  .cp-evaluation-plan-web-doc-example-answer-readonly,
  .cp-evaluation-plan-web-doc-example-answer-input-shell,
  .cp-evaluation-plan-web-doc-example-answer-body .cp-evaluation-plan-web-doc-cell-input-shell {
    flex: 1 1 auto !important;
    min-height: 100% !important;
    height: 100% !important;
  }
  .cp-evaluation-plan-web-doc-table {
    page-break-inside: auto;
    break-inside: auto;
  }
`;

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function mmToPx(mm: number): number {
  return (mm * 96) / 25.4;
}

function getDocumentPage(root: HTMLElement): HTMLElement {
  const page = root.querySelector('.cp-evaluation-plan-web-doc-page');
  if (!page) {
    throw new Error('보낼 문서 데이터가 없습니다.');
  }
  return page as HTMLElement;
}

function cloneDocumentPage(page: HTMLElement): HTMLElement {
  const clone = page.cloneNode(true) as HTMLElement;
  const width = page.clientWidth || page.offsetWidth || 794;
  clone.style.width = `${width}px`;
  prepareEvaluationPlanWebDocumentPageClone(clone);
  return clone;
}

function measureDocumentPagePrintScale(page: HTMLElement): number {
  const a4W = mmToPx(A4_WIDTH_MM);
  const a4H = mmToPx(A4_HEIGHT_MM);
  const rect = page.getBoundingClientRect();
  const w = rect.width || page.offsetWidth || page.scrollWidth;
  const h = rect.height || page.offsetHeight || page.scrollHeight;
  if (w <= 0 || h <= 0) return 1;
  return Math.min(1, a4W / w, a4H / h);
}

function buildEvaluationPlanWebDocumentPrintHtml(page: HTMLElement, title: string): string {
  const clone = cloneDocumentPage(page);
  const scale = measureDocumentPagePrintScale(page);
  const scaledInner =
    scale < 0.999
      ? `<div class="cp-evaluation-plan-web-doc-print-scaled" style="transform:scale(${scale}); transform-origin:top center;">${clone.outerHTML}</div>`
      : clone.outerHTML;

  return `<!DOCTYPE html>
<html lang="ko">
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    <style>
      ${EVALUATION_PLAN_WEB_DOCUMENT_CAPTURE_CSS}
      ${EVALUATION_PLAN_WEB_DOC_PRINT_SHELL_CSS}
    </style>
  </head>
  <body>
    <div class="cp-evaluation-plan-web-doc-print-port">${scaledInner}</div>
  </body>
</html>`;
}

function mountOffscreenDocumentPage(page: HTMLElement): { host: HTMLDivElement; mountedPage: HTMLElement } {
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.cssText =
    'position:fixed;left:-100000px;top:0;z-index:-1;width:794px;background:#fff;overflow:visible;pointer-events:none;';

  const style = document.createElement('style');
  style.textContent = `${EVALUATION_PLAN_WEB_DOCUMENT_CAPTURE_CSS}${EVALUATION_PLAN_WEB_DOC_PRINT_SHELL_CSS}`;
  host.appendChild(style);

  const port = document.createElement('div');
  port.className = 'cp-evaluation-plan-web-doc-print-port';
  const clone = cloneDocumentPage(page);
  port.appendChild(clone);
  host.appendChild(port);

  document.body.appendChild(host);
  balanceEvaluationPlanWebDocumentA4Layout(clone);
  return { host, mountedPage: clone };
}

/** 화면 문서 페이지만 격리 HTML로 인쇄(텍스트·표 구조 유지) */
export function printEvaluationPlanWebDocumentFromRoot(root: HTMLElement, title: string): void {
  const page = getDocumentPage(root);
  printStandaloneHtmlDocument(buildEvaluationPlanWebDocumentPrintHtml(page, title));
}

/** 문서 페이지만 A4 PDF로 저장 */
export async function saveEvaluationPlanWebDocumentAsPdfFromRoot(params: {
  root: HTMLElement;
  fileName: string;
}): Promise<void> {
  const page = getDocumentPage(params.root);
  const { host, mountedPage } = mountOffscreenDocumentPage(page);

  try {
    await new Promise<void>((resolve) => {
      window.requestAnimationFrame(() => resolve());
    });

    const canvas = await html2canvas(mountedPage, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      logging: false,
    });
    if (canvas.width < 2 || canvas.height < 2) {
      throw new Error('PDF 캡처에 실패했습니다.');
    }

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const imgData = canvas.toDataURL('image/png');

    let drawW = pageW;
    let drawH = (canvas.height * drawW) / canvas.width;
    if (drawH > pageH) {
      drawH = pageH;
      drawW = (canvas.width * drawH) / canvas.height;
    }
    const x = (pageW - drawW) / 2;
    const y = (pageH - drawH) / 2;
    pdf.addImage(imgData, 'PNG', x, y, drawW, drawH);
    pdf.save(params.fileName);
  } finally {
    host.remove();
  }
}
